import type { RefObject } from 'react'
import { useCallback, useRef } from 'react'
import type { CruiseLineSelectRef } from './CruiseLineSelect/CruiseLineSelect'
import CruiseLineSelect from './CruiseLineSelect/CruiseLineSelect'
import type { CruiseWhenSelectRef } from './CruiseWhenSelect/CruiseWhenSelect'
import CruiseWhenSelect from './CruiseWhenSelect/CruiseWhenSelect'
import type { CruiseLocationFilterRef } from './CruiseLocationFilter/CruiseLocationFilter'
import CruiseDestinationFilter from './CruiseLocationFilter/CruiseLocationFilter'
import noop from 'lib/function/noop'
import type { CruiseDepartureSelectRef } from 'components/Cruises/SearchPage/Filters/Inputs/CruiseDepartureSelect/CruiseDepartureSelect'
import CruiseDepartureSelect from 'components/Cruises/SearchPage/Filters/Inputs/CruiseDepartureSelect/CruiseDepartureSelect'
import { Experiments } from 'constants/optimizely'
import useExperiment from 'hooks/Optimizely/useExperiment'

interface Props {
  onChange?: (values: App.CruiseGlobalFilters) => void
  /**
   * Necessary for the non-`drawerMode` to work
   */
  dropdownAnchorRef?: RefObject<HTMLElement | null>
  drawerMode?: boolean
  isCruiselineLandingPage?: boolean
  disableAutoFocus?: boolean
}

function CruiseSearchInputs(props: Props) {
  const { drawerMode, onChange = noop, dropdownAnchorRef, isCruiselineLandingPage, disableAutoFocus } = props
  const destinationRef = useRef<CruiseLocationFilterRef>(null)
  const departureRef = useRef<CruiseDepartureSelectRef>(null)
  const cruiseLineRef = useRef<CruiseLineSelectRef>(null)
  const whenRef = useRef<CruiseWhenSelectRef>(null)

  const { isAnyVariant: autoFocusEnabled } = useExperiment(Experiments.cruiseAutoFocusNextFilter)

  const onDestinationChange = useCallback((values: App.CruiseGlobalFilters) => {
    onChange(values)
    if (autoFocusEnabled) {
      departureRef.current?.focus()
    }
  }, [onChange, autoFocusEnabled])

  const onDepartureChange = useCallback((values: App.CruiseGlobalFilters) => {
    onChange(values)
    if (autoFocusEnabled) {
      if (!isCruiselineLandingPage && cruiseLineRef.current) {
        cruiseLineRef.current.focus()
      } else {
        whenRef.current?.focus()
      }
    }
  }, [onChange, autoFocusEnabled, isCruiselineLandingPage])

  const onLineChange = useCallback((values: App.CruiseGlobalFilters) => {
    onChange(values)
    if (autoFocusEnabled) {
      whenRef.current?.focus()
    }
  }, [onChange, autoFocusEnabled])

  return <>
    <CruiseDestinationFilter
      ref={destinationRef}
      onChange={onDestinationChange}
      drawerMode={drawerMode}
      dropdownAnchorRef={dropdownAnchorRef}
    />

    <CruiseDepartureSelect
      ref={departureRef}
      onChange={onDepartureChange}
      drawerMode={drawerMode}
      dropdownAnchorRef={dropdownAnchorRef}
      disableAutoFocus={disableAutoFocus}
    />

    {!isCruiselineLandingPage && <CruiseLineSelect
      ref={cruiseLineRef}
      onChange={onLineChange}
      dropdownAnchorRef={dropdownAnchorRef!}
      drawerMode={drawerMode}
    />}

    <CruiseWhenSelect
      ref={whenRef}
      onChange={onChange}
      dropdownAnchorRef={dropdownAnchorRef}
      drawerMode={drawerMode}
    />
  </>
}

export default CruiseSearchInputs
